<template>
  <div
    class="dg-slider"
    :style="{
      '--input-value-percent': `${selectedValue}%`,
      '--input-value-decimal': selectedValue / 100,
    }"
  >
    <p class="dg-slider__text">{{ selectedValue * 2 }} guides <br />/ month</p>

    <div class="dg-slider__input-wrapper">
      <input
        type="range"
        min="1"
        max="99"
        :value="selectedValue"
        class="dg-slider__input"
        @input="updateChoice"
      />
    </div>

    <p class="dg-slider__label">
      <strong class="dg-slider__label-value">{{ selectedValue }}</strong>
      <span class="dg-slider__label-text">min</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { PublicInterface } from '@/models/interfaces'
import { QuestionnaireView } from '@/models/onboarding'
import { DailyGoalChoice } from '@/models/onboarding.types'

const props = defineProps<{
  view: PublicInterface<QuestionnaireView>
}>()

const selectedValue = computed(() => {
  return props.view.currentChoice as DailyGoalChoice
})

function updateChoice(event: Event): void {
  const target = event.target as HTMLInputElement
  props.view.setCurrentChoice(parseInt(target.value))
}
</script>

<style lang="scss" scoped>
$track-height: 4px;
$thumb-size: 28px;
$notch-height: 16px;

.dg-slider {
  width: 100%;
  position: relative;

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    height: $thumb-size;

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 0;
      width: 1px;
      height: $notch-height;
      background: $c-black-13;
      pointer-events: none;
    }

    &:before {
      left: 0;
    }

    &:after {
      right: 0;
    }
  }

  &__input {
    appearance: none;
    position: relative;
    z-index: 0;
    width: 100%;
    height: $track-height;
    background: $c-black-13;
    cursor: pointer;

    // This is the colored part behind the selected point in the slider.
    &:before {
      content: '';
      position: absolute;
      width: var(--input-value-percent);
      height: 100%;
      background: $c-yellow;
      pointer-events: none;
    }

    &::-webkit-slider-runnable-track,
    &::-moz-range-track {
      appearance: none;
      background: $c-black-13;
      height: $track-height;
    }

    &::-webkit-slider-thumb {
      appearance: none;
      position: relative;
      width: $thumb-size;
      aspect-ratio: 1/1;
      background: $c-bg;
      border-radius: 999px;
      pointer-events: all;
      z-index: 1;
      border: 6px solid $c-yellow;
    }
  }

  &__text {
    display: block;
    width: 112px;
    padding-inline: 16px;
    padding-block: 20px 29px;
    margin-bottom: 16px;
    clip-path: path(
      'M108 -1.15241e-07C110.209 -5.15951e-08 112 1.79086 112 4L112 59.4656C112 63.0374 109.632 66.1765 106.198 67.1578L58.1978 80.8721C56.7613 81.2825 55.2387 81.2825 53.8022 80.8721L5.80223 67.1578C2.36782 66.1765 -2.75545e-06 63.0374 -2.59932e-06 59.4656L-1.74846e-07 4C-7.82811e-08 1.79086 1.79086 -3.17515e-06 4 -3.1115e-06L108 -1.15241e-07Z'
    );
    // This element's horizontal position has to follow the input's
    // thumb button. The calc for the position is a bit complicated,
    // so it's broken down in three steps:
    //
    // This is the position when the input is at 100%.
    // `--onboarding-inner-width` is the parent's width.
    --max-position-left: calc(var(--onboarding-inner-width) - 18px);
    // Get the equivalent percentage of the maximum position.
    --position-left: calc(
      var(--max-position-left) * var(--input-value-decimal)
    );
    // Offset it by a few pixels to center the container.
    --position-left-with-offset: calc(var(--position-left) - 48px);
    transform: translateX(var(--position-left-with-offset));
    background: $c-border-7;
    font-size: 14px;
    line-height: 16px;
    text-align: center;
    color: $c-black-15;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 4px;
    width: fit-content;
    font-size: 32px;
    // This element's horizontal position has to follow the input's
    // thumb button. The calc for the position is a bit complicated,
    // so it's broken down in three steps:
    //
    // This is the position when the input is at 100%.
    // `--onboarding-inner-width` is the parent's width.
    --max-position-left: calc(var(--onboarding-inner-width) - 1ch);
    // Get the equivalent percentage of the maximum position.
    --position-left: calc(
      var(--max-position-left) * var(--input-value-decimal)
    );
    // Offset it by a few pixels to center on the number in the label.
    --position-left-with-offset: calc(var(--position-left) - 0.5ch + 2px);
    transform: translateX(var(--position-left-with-offset));
    margin-top: 20px;
    color: $c-black-2;
  }

  &__label-text {
    font-size: 18px;
    line-height: 20px;
  }

  &__label-value {
    width: 2ch;
    text-align: center;
    font-weight: 500;
    font-size: 32px;
    line-height: 1;
    letter-spacing: 0.1em;
  }
}
</style>
